/**
 * Helpers for the silent-refresh dance (H §4.1–4.2).
 *
 *  - `bootRefresh()` runs at cold load and on `/auth/return`: `GET /api/auth/csrf` →
 *    `POST /api/auth/refresh` (the csrf step is `client.ts`'s, before every refresh). On
 *    success it stores the bearer, its expiry and the CSRF token. It never calls
 *    `/api/auth/session/exchange` — that rejects reseller sessions.
 *  - `proactiveRefresh()` is the timer's handler (~60 s before expiry, armed by `authStore`);
 *    it shares the transport-level single flight with the factory's reactive 401 refresh.
 *  - `stashDeepLink` / `popDeepLink` keep the intended path across the login round-trip
 *    (sessionStorage — the deep-link PATH only, never the token). Only a same-origin path
 *    (a single leading `/`, no scheme, no `//`) is ever stored or restored (H §9.1).
 *  - `redirectToCentralizedLogin()` stashes the deep link and navigates to the bootstrapped
 *    `loginUrl` (used verbatim, ported from dink-admin DNKADM-0274) with
 *    `?returnUrl=<origin>/auth/return` appended — after checking it is https (H §9.2).
 */
import { authApi } from '@/api/auth';
import { ApiError, backendCode } from '@/api/errors';
import type { AuthTokenResponse } from '@/api/types';
import {
  getSessionEpoch,
  setAccessTokenIfCurrent,
  setCsrfTokenIfCurrent,
  signOut,
  type SessionEndReason,
} from './authStore';
import { env } from '@/config/env';
import { getBootstrap, isSafeBootstrapUrl } from '@/config/bootstrap';

const DEEP_LINK_KEY = 'tmm_reseller_admin.deepLink';

/** `ok` — signed in; `signin` — no session, go to centralized login; otherwise the session
 *  ended for a reason that must be shown, never redirected past. */
export type BootResult = 'ok' | 'signin' | Exclude<SessionEndReason, 'expired'>;

function storeTokens(body: AuthTokenResponse, epoch: number): boolean {
  const expiresAt =
    body.expiresAt ??
    (typeof body.expiresIn === 'number' && body.expiresIn > 0
      ? new Date(Date.now() + body.expiresIn * 1000).toISOString()
      : null);
  if (body.csrfToken) setCsrfTokenIfCurrent(epoch, body.csrfToken);
  return setAccessTokenIfCurrent(epoch, body.accessToken, expiresAt);
}

export async function bootRefresh(): Promise<BootResult> {
  const epoch = getSessionEpoch();
  try {
    const body = await authApi.refresh();
    if (!body?.accessToken) return 'signin';
    if (!storeTokens(body, epoch)) return 'signin';
    return 'ok';
  } catch (err) {
    // `client.ts` has already ended the session with its reason for a session-ending code.
    const code = backendCode(err);
    if (code === 'RESELLER_ACCESS_REVOKED') return 'revoked';
    if (code === 'REFRESH_ORIGIN_UNROUTABLE' || code === 'INVALID_ORIGIN') return 'misconfigured';
    // 401 / 403 / network all mean "not signed in" at boot.
    return 'signin';
  }
}

/**
 * One proactive refresh. A dead session (401) expires it now; a session-ending code is handled by
 * `client.ts`; anything transient is left for the reactive 401 path. Resolves once the outcome is
 * applied (exported so a test can await it).
 */
export function runProactiveRefresh(): Promise<void> {
  const epoch = getSessionEpoch();
  return authApi.refresh().then(
    (body) => {
      if (body?.accessToken) storeTokens(body, epoch);
    },
    (err: unknown) => {
      if (!(err instanceof ApiError) || err.code !== 'SESSION_EXPIRED') return;
      // `client.ts` already ended the session with its own reason for these.
      const code = backendCode(err);
      if (
        code === 'RESELLER_ACCESS_REVOKED' ||
        code === 'REFRESH_ORIGIN_UNROUTABLE' ||
        code === 'INVALID_ORIGIN'
      ) {
        return;
      }
      signOut({ reason: 'expired' });
    },
  );
}

/** The proactive-refresh timer's handler. */
export function proactiveRefresh(): void {
  void runProactiveRefresh();
}

/** A same-origin path: one leading `/`, not `//`, no backslash (browsers read `/\` as `//`). */
export function isSafeDeepLink(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^\/(?!\/)/.test(value) && !value.includes('\\');
}

export function stashDeepLink(path: string): void {
  // Never stash loop paths — they would bounce the user back to login.
  if (!isSafeDeepLink(path) || path === '/' || path.startsWith('/auth/')) return;
  try {
    sessionStorage.setItem(DEEP_LINK_KEY, path);
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function popDeepLink(): string | null {
  try {
    const v = sessionStorage.getItem(DEEP_LINK_KEY);
    sessionStorage.removeItem(DEEP_LINK_KEY);
    return isSafeDeepLink(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * `loginUrl` comes from the bootstrapped platform singleton and is used
 * VERBATIM — it carries no query string, so appending `?returnUrl=` is
 * always correct (ported from dink-admin DNKADM-0274). Never parsed, rebuilt,
 * or normalized.
 */
export function centralizedLoginUrl(returnUrl: string): string {
  const { loginUrl } = getBootstrap();
  return `${loginUrl}?returnUrl=${encodeURIComponent(returnUrl)}`;
}

/** `location.assign` a bootstrapped URL only when it is https (H §9.2). */
export function assignBootstrappedUrl(url: string): void {
  if (!isSafeBootstrapUrl(url)) {
    console.error('Refusing to navigate to a non-https bootstrapped URL.');
    return;
  }
  if (typeof window !== 'undefined') window.location.assign(url);
}

export function redirectToCentralizedLogin(currentPath: string): void {
  stashDeepLink(currentPath);
  assignBootstrappedUrl(centralizedLoginUrl(`${env.appOrigin}/auth/return`));
}
